import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, CheckCircle, XCircle, Info, Lightbulb, ExternalLink, AlertTriangle } from 'lucide-react';
import { Card, PageHeader, Badge } from '../components/Card';
import { insuranceTypes } from '../data/inspectionData';
import { useApp } from '../context/AppContext';

function CalloutBox({ children }) {
  return (
    <div style={{
      background: 'rgba(232, 151, 44, 0.1)',
      border: '1px solid rgba(232, 151, 44, 0.3)',
      borderRadius: 8,
      padding: '12px 16px',
      marginTop: 12,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }}>
      <Lightbulb size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
      <span style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.6, fontWeight: 500 }}>{children}</span>
    </div>
  );
}

function SectionsLearnMore({ sections }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
      {sections.map((section, i) => (
        <div key={i} style={{ marginBottom: i < sections.length - 1 ? 8 : 0 }}>
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--gray-100)',
              border: '1px solid var(--gray-200)',
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--gray-800)',
            }}
          >
            {section.title}
            {openIndex === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {openIndex === i && (
            <div style={{ padding: '12px 14px', borderLeft: '2px solid var(--gray-200)', marginLeft: 12, marginTop: 4 }}>
              <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: 8 }}>
                {section.content}
              </p>
              {section.bullet && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  fontSize: 13, color: 'var(--gray-700)', fontWeight: 500, marginBottom: 4,
                }}>
                  <span style={{ color: 'var(--green)', flexShrink: 0 }}>-</span>
                  <span style={{ lineHeight: 1.5 }}>{section.bullet}</span>
                </div>
              )}
              {section.callout && <CalloutBox>{section.callout}</CalloutBox>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function NarrativeLearnMore({ learnMore }) {
  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
      {learnMore.tagline && (
        <p style={{
          fontSize: 14, fontStyle: 'italic', color: 'var(--gray-600)',
          lineHeight: 1.6, marginBottom: 20, paddingBottom: 12,
          borderBottom: '1px solid var(--gray-100)',
        }}>
          {learnMore.tagline}
        </p>
      )}

      {learnMore.sections.map((section, i) => (
        <div key={i} style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--gray-800)' }}>
            {section.title}
          </h4>
          <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: 8 }}>
            {section.content}
          </p>

          {section.bullets && (
            <div style={{ paddingLeft: 8, marginBottom: 8 }}>
              {section.bullets.map((bullet, j) => (
                <div key={j} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '3px 0', fontSize: 13, color: 'var(--gray-600)',
                }}>
                  <span style={{ color: 'var(--green)', fontWeight: 600, flexShrink: 0 }}>-</span>
                  <span style={{ lineHeight: 1.5 }}>{bullet}</span>
                </div>
              ))}
            </div>
          )}

          {section.callout && <CalloutBox>{section.callout}</CalloutBox>}

          {section.highlight && (
            <div style={{
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: 8,
              padding: '12px 16px',
              marginTop: 12,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}>
              <AlertTriangle size={16} color="var(--blue)" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 13, color: 'var(--blue)', lineHeight: 1.6, fontWeight: 600 }}>
                {section.highlight}
              </span>
            </div>
          )}
        </div>
      ))}

      {learnMore.ctaUrl && (
        <a
          href={learnMore.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--primary)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
            marginTop: 4,
          }}
        >
          {learnMore.ctaText} <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
}

function StandardLearnMore({ learnMore }) {
  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <CheckCircle size={16} color="var(--green)" />
          <h4 style={{ fontSize: 14, fontWeight: 600 }}>What It Covers</h4>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6, paddingLeft: 24 }}>
          {learnMore.whatItCovers}
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <XCircle size={16} color="var(--red)" />
          <h4 style={{ fontSize: 14, fontWeight: 600 }}>What It Does NOT Cover</h4>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6, paddingLeft: 24 }}>
          {learnMore.whatItDoesNotCover}
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Info size={16} color="var(--blue)" />
          <h4 style={{ fontSize: 14, fontWeight: 600 }}>How to Get It</h4>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.6, paddingLeft: 24 }}>
          {learnMore.howToGetIt}
        </p>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Lightbulb size={16} color="var(--primary)" />
          <h4 style={{ fontSize: 14, fontWeight: 600 }}>Tips</h4>
        </div>
        <div style={{ paddingLeft: 24 }}>
          {learnMore.tips.map((tip, j) => (
            <div key={j} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '4px 0', fontSize: 13, color: 'var(--gray-600)',
            }}>
              <span style={{ color: 'var(--green)', fontWeight: 600, flexShrink: 0 }}>-</span>
              <span style={{ lineHeight: 1.5 }}>{tip}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InsuranceCard({ ins, isOttawa }) {
  const [expanded, setExpanded] = useState(false);

  const renderLearnMore = () => {
    if (!ins.learnMore) return null;
    if (ins.learnMoreType === 'sections') {
      const filteredSections = ins.learnMore.sections.filter(s => !s.ottawaOnly || isOttawa);
      return <SectionsLearnMore sections={filteredSections} />;
    }
    if (ins.learnMoreType === 'narrative') return <NarrativeLearnMore learnMore={ins.learnMore} />;
    return <StandardLearnMore learnMore={ins.learnMore} />;
  };

  return (
    <Card style={{
      display: 'flex', flexDirection: 'column',
      border: '1px solid var(--gray-200)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, flex: 1, marginRight: 8 }}>{ins.title}</h3>
        <Badge color={ins.badgeColor}>{ins.badge}</Badge>
      </div>

      <div style={{ fontSize: 14, marginBottom: 12 }}>
        <div style={{ marginBottom: 4 }}>
          <strong>Protects:</strong> {ins.protects}
        </div>
        <div style={{ marginBottom: 4 }}>
          <strong>Cost:</strong> {ins.cost}
        </div>
        <div>
          <strong>When:</strong> {ins.when}
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--gray-600)', flex: 1, marginBottom: 16 }}>
        {ins.description}
      </p>

      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', color: 'var(--primary)',
          fontWeight: 500, fontSize: 14, padding: 0,
        }}
      >
        {expanded ? 'Show Less' : 'Learn More'} {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && renderLearnMore()}
    </Card>
  );
}

export default function Insurance() {
  const { settings } = useApp();
  const isOttawa = settings.isOttawa === true;
  return (
    <div>
      <PageHeader
        icon={<Shield size={22} />}
        title="Insurance Guide"
        subtitle="Every type of insurance you need to understand before you get the keys."
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        {insuranceTypes.map((ins, i) => (
          <InsuranceCard key={i} ins={ins} isOttawa={isOttawa} />
        ))}
      </div>
    </div>
  );
}
